// src/components/manager-v2/PerformanceReviewModal.js
import React, { useState, useEffect } from 'react';
import { X, Save, Send, Clock, Edit2, AlertCircle, Star } from 'lucide-react';
import performanceService from '../../services/performance.service';

const PerformanceReviewModal = ({ isOpen, onClose, employee, existingReview = null, mode = 'new', selfReviewData = null }) => {
    const [formData, setFormData] = useState({
        employee_id: '',
        template_id: '',
        review_cycle: 'monthly',
        review_period_start: '',
        review_period_end: '',
        technical_skills: 0,
        communication_skills: 0,
        teamwork: 0,
        problem_solving: 0,
        leadership: 0,
        strengths: '',
        areas_for_improvement: '',
        goals_for_next_period: '',
        manager_comments: '',
        overall_rating: 0
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [previousReviews, setPreviousReviews] = useState([]);
    const [showPreviousReviews, setShowPreviousReviews] = useState(false);
    const [selectedReview, setSelectedReview] = useState(null);

    // Load existing review data or previous reviews
    useEffect(() => {
        const initializeModal = async () => {
            if (isOpen && employee) {
                if (mode === 'edit') {
                    // Show loading state immediately when in edit mode
                    setShowPreviousReviews(true);
                    fetchPreviousReviews();
                } else if (existingReview) {
                    loadReviewData(existingReview);
                } else {
                    resetForm();
                    // Pre-fill from self-review data if available
                    if (selfReviewData) {
                        preFillFromSelfReview(selfReviewData);
                    }
                    await fetchEmployeeTemplate();
                    setShowPreviousReviews(false);
                }
            } else if (!isOpen) {
                // Reset state when modal closes
                setShowPreviousReviews(false);
                setPreviousReviews([]);
                setSelectedReview(null);
                setError(null);
            }
        };

        initializeModal();
    }, [isOpen, employee, mode, existingReview, selfReviewData]);

    const fetchEmployeeTemplate = async () => {
        try {
            // Fetch the employee's template from their profile
            const response = await performanceService.getEmployeeTemplate(employee.id);
            if (response && response.template_id) {
                setFormData(prev => ({ ...prev, template_id: response.template_id }));
            }
        } catch (err) {
            console.error('Error fetching employee template:', err);
            // Template is not critical for draft, so just log the error
        }
    };

    const preFillFromSelfReview = (selfReview) => {
        // Pre-fill form data with review period and cycle from self-review
        // Format dates to YYYY-MM-DD for date inputs
        const formatDate = (dateString) => {
            if (!dateString) return '';
            return dateString.split('T')[0];
        };

        setFormData(prev => ({
            ...prev,
            review_cycle: selfReview.review_cycle || prev.review_cycle,
            review_period_start: formatDate(selfReview.review_period_start) || prev.review_period_start,
            review_period_end: formatDate(selfReview.review_period_end) || prev.review_period_end,
            employee_id: selfReview.employee_id || employee.id
        }));
    };

    const fetchPreviousReviews = async () => {
        try {
            setLoading(true);
            const reviews = await performanceService.getEmployeeReviews(employee.id);
            setPreviousReviews(reviews);

            // If no previous reviews exist, automatically show the new review form
            if (reviews.length === 0) {
                setShowPreviousReviews(false);
            } else {
                setShowPreviousReviews(true);
            }
        } catch (err) {
            setError('Failed to load previous reviews');
            console.error('Error fetching previous reviews:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadReviewData = (review) => {
        setFormData({
            employee_id: review.employee_id || employee.id,
            template_id: review.template_id || '',
            review_cycle: review.review_cycle || 'monthly',
            review_period_start: review.review_period_start || '',
            review_period_end: review.review_period_end || '',
            technical_skills: review.technical_skills || 0,
            communication_skills: review.communication_skills || 0,
            teamwork: review.teamwork || 0,
            problem_solving: review.problem_solving || 0,
            leadership: review.leadership || 0,
            strengths: review.strengths || '',
            areas_for_improvement: review.areas_for_improvement || '',
            goals_for_next_period: review.goals_for_next_period || '',
            manager_comments: review.manager_comments || '',
            overall_rating: review.overall_rating || 0
        });
        setSelectedReview(review);
        setShowPreviousReviews(false);
    };

    const resetForm = () => {
        const today = new Date();
        const sixMonthsAgo = new Date(today);
        sixMonthsAgo.setMonth(today.getMonth() - 6);

        setFormData({
            employee_id: employee?.id || '',
            template_id: '',
            review_cycle: 'monthly',
            review_period_start: sixMonthsAgo.toISOString().split('T')[0],
            review_period_end: today.toISOString().split('T')[0],
            technical_skills: 0,
            communication_skills: 0,
            teamwork: 0,
            problem_solving: 0,
            leadership: 0,
            strengths: '',
            areas_for_improvement: '',
            goals_for_next_period: '',
            manager_comments: '',
            overall_rating: 0
        });
        setSelectedReview(null);
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        // Auto-calculate overall rating when skill ratings change
        if (['technical_skills', 'communication_skills', 'teamwork', 'problem_solving', 'leadership'].includes(field)) {
            const updatedData = { ...formData, [field]: parseFloat(value) };
            const avg = (
                updatedData.technical_skills +
                updatedData.communication_skills +
                updatedData.teamwork +
                updatedData.problem_solving +
                updatedData.leadership
            ) / 5;
            setFormData(prev => ({ ...prev, [field]: parseFloat(value), overall_rating: parseFloat(avg.toFixed(1)) }));
        }
    };

    const handleSaveDraft = async () => {
        try {
            setLoading(true);
            setError(null);

            const reviewData = {
                ...formData,
                employee_id: employee.id,
                status: 'draft',
                // Ensure all numeric fields are properly parsed
                technical_skills: parseFloat(formData.technical_skills) || 0,
                communication_skills: parseFloat(formData.communication_skills) || 0,
                teamwork: parseFloat(formData.teamwork) || 0,
                problem_solving: parseFloat(formData.problem_solving) || 0,
                leadership: parseFloat(formData.leadership) || 0,
                overall_rating: parseFloat(formData.overall_rating) || 0
            };

            if (selectedReview) {
                await performanceService.updateReview(selectedReview.id, reviewData);
            } else {
                await performanceService.createManagerReview(reviewData);
            }

            alert('Review saved as draft successfully!');
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save review');
            console.error('Error saving review:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            setError(null);

            const reviewData = {
                ...formData,
                employee_id: employee.id,
                status: 'completed',
                // Ensure all numeric fields are properly parsed
                technical_skills: parseFloat(formData.technical_skills) || 0,
                communication_skills: parseFloat(formData.communication_skills) || 0,
                teamwork: parseFloat(formData.teamwork) || 0,
                problem_solving: parseFloat(formData.problem_solving) || 0,
                leadership: parseFloat(formData.leadership) || 0,
                overall_rating: parseFloat(formData.overall_rating) || 0
            };

            if (selectedReview) {
                await performanceService.updateReview(selectedReview.id, reviewData);
            } else {
                await performanceService.createManagerReview(reviewData);
            }

            alert('Review submitted to admin successfully!');
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to submit review');
            console.error('Error submitting review:', err);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                            {showPreviousReviews ? 'Previous Reviews' : selectedReview ? 'Edit Performance Review' : 'New Performance Review'}
                        </h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {employee.first_name} {employee.last_name} - {employee.designation}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {error && (
                        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
                            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                        </div>
                    )}

                    {/* Previous Reviews List */}
                    {showPreviousReviews && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Select a review to edit</h3>
                                <button
                                    onClick={() => setShowPreviousReviews(false)}
                                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                                >
                                    Create New Review
                                </button>
                            </div>

                            {loading ? (
                                <div className="text-center py-12">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">Loading reviews...</p>
                                </div>
                            ) : previousReviews.length === 0 ? (
                                <div className="text-center py-12">
                                    <div className="bg-gray-100 dark:bg-gray-800 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                        <AlertCircle className="w-8 h-8 text-gray-400" />
                                    </div>
                                    <p className="text-gray-900 dark:text-white font-medium mb-1">No previous reviews found</p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                                        This employee doesn't have any performance reviews yet.
                                    </p>
                                    <button
                                        onClick={() => setShowPreviousReviews(false)}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                    >
                                        Create First Review
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {previousReviews.map((review) => {
                                        const createdDate = new Date(review.created_at);
                                        const startDate = new Date(review.review_period_start);
                                        const endDate = new Date(review.review_period_end);

                                        return (
                                            <div
                                                key={review.id}
                                                onClick={() => loadReviewData(review)}
                                                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors hover:border-blue-400 dark:hover:border-blue-600"
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <p className="font-semibold text-gray-900 dark:text-white">
                                                                {startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                            </p>
                                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                                review.status === 'completed'
                                                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                                    : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                                            }`}>
                                                                {review.status.charAt(0).toUpperCase() + review.status.slice(1)}
                                                            </span>
                                                        </div>

                                                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                                                            <div className="flex items-center gap-2">
                                                                <Star className="w-4 h-4 text-yellow-500" />
                                                                <span className="text-gray-600 dark:text-gray-400">
                                                                    Rating: <span className="font-semibold text-gray-900 dark:text-white">{parseFloat(review.overall_rating).toFixed(1)}/5.0</span>
                                                                </span>
                                                            </div>
                                                            <div className="text-gray-500 dark:text-gray-500 text-xs">
                                                                Created: {createdDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                            </div>
                                                        </div>

                                                        {review.manager_comments && (
                                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 line-clamp-1">
                                                                {review.manager_comments}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <Edit2 className="w-5 h-5 text-gray-400 flex-shrink-0 ml-3" />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Review Form */}
                    {!showPreviousReviews && (
                        <form className="space-y-6">
                            {/* Review Cycle */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Review Cycle *
                                </label>
                                <select
                                    value={formData.review_cycle}
                                    onChange={(e) => handleInputChange('review_cycle', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                >
                                    <option value="monthly">Monthly</option>
                                    <option value="quarterly">Quarterly</option>
                                    <option value="annual">Annual</option>
                                </select>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    One {formData.review_cycle} review per employee per period
                                </p>
                            </div>

                            {/* Review Period */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Review Period Start
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.review_period_start}
                                        onChange={(e) => handleInputChange('review_period_start', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Review Period End
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.review_period_end}
                                        onChange={(e) => handleInputChange('review_period_end', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    />
                                </div>
                            </div>

                            {/* Skill Ratings */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Skill Ratings (0-5)</h3>
                                <div className="space-y-4">
                                    {[
                                        { key: 'technical_skills', label: 'Technical Skills' },
                                        { key: 'communication_skills', label: 'Communication Skills' },
                                        { key: 'teamwork', label: 'Teamwork & Collaboration' },
                                        { key: 'problem_solving', label: 'Problem Solving' },
                                        { key: 'leadership', label: 'Leadership' }
                                    ].map((skill) => (
                                        <div key={skill.key}>
                                            <div className="flex items-center justify-between mb-2">
                                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    {skill.label}
                                                </label>
                                                <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                                                    {formData[skill.key]}/5
                                                </span>
                                            </div>
                                            <input
                                                type="range"
                                                min="0"
                                                max="5"
                                                step="0.5"
                                                value={formData[skill.key]}
                                                onChange={(e) => handleInputChange(skill.key, e.target.value)}
                                                className="w-full"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Overall Rating (Auto-calculated) */}
                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Overall Rating</span>
                                    <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                        {formData.overall_rating}/5.0
                                    </span>
                                </div>
                            </div>

                            {/* Text Areas */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Key Strengths
                                </label>
                                <textarea
                                    value={formData.strengths}
                                    onChange={(e) => handleInputChange('strengths', e.target.value)}
                                    rows="3"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    placeholder="What are the employee's key strengths?"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Areas for Improvement
                                </label>
                                <textarea
                                    value={formData.areas_for_improvement}
                                    onChange={(e) => handleInputChange('areas_for_improvement', e.target.value)}
                                    rows="3"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    placeholder="What areas need improvement?"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Goals for Next Review Period
                                </label>
                                <textarea
                                    value={formData.goals_for_next_period}
                                    onChange={(e) => handleInputChange('goals_for_next_period', e.target.value)}
                                    rows="3"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    placeholder="What goals should be set for the next period?"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Manager Comments
                                </label>
                                <textarea
                                    value={formData.manager_comments}
                                    onChange={(e) => handleInputChange('manager_comments', e.target.value)}
                                    rows="4"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    placeholder="Additional comments or feedback..."
                                />
                            </div>
                        </form>
                    )}
                </div>

                {/* Footer */}
                {!showPreviousReviews && (
                    <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <div className="flex gap-3">
                            <button
                                onClick={handleSaveDraft}
                                disabled={loading}
                                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                            >
                                <Save className="w-4 h-4" />
                                Save Draft
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={loading}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                            >
                                <Send className="w-4 h-4" />
                                Submit Review
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PerformanceReviewModal;
